'use client';

import { FC, useMemo, useState } from 'react';

import { User, UserGroups } from '@ravago/shared/authentication';
import { isFeatureEnabled } from '@ravago/shared/next-data-access';
import { QuotationUtils } from '@ravago/shared/next-data-access/client';
import { FeatureToggle } from '@ravago/shared/page-data/models/config';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { OrderedProduct, RelatedDocument } from '@ravago/shared/page-data/models/elements';
import { CustomerDataPage } from '@ravago/shared/page-data/models/pages';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { saveAs } from 'file-saver';
import { firstBy } from 'thenby';

import { useDialogContext } from '../../../../hooks/use-dialog-context';
import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { DocumentNameUtils } from '../../../../utils/document-name/document-name.utils';
import { getLanguageCode, getLanguageNameFromLanguageCode } from '../../../../utils/locale/locale.utils';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { Link } from '../../../shared/link/link';
import { ShareAnchor } from '../../../shared/share-anchor/share-anchor';

interface Props {
   isPublicTdsDownloadFeatureEnabled: boolean;
   isPublicShowTdsUomToggleEnabled: boolean;
   documentAnchor: string | undefined;
   actions: {
      downloadPublicTdsDocument: (
         documentLanguage: string,
         ulProductId: string,
         correlationId: string,
         documentUOM?: 'metric' | 'imperial'
      ) => Promise<string>;
      downloadDocument: (downloadEndpoint: string, accessaccessToken: string, correlationId: string) => Promise<string | undefined>;
      getAllOrderedProducts: (accessaccessToken: string) => Promise<CustomerDataPage<OrderedProduct>>;
   };
   productId?: string;
   productName?: string;
   locale: string;
   uomCollection: Array<NormalizedUnitOfMeasure> | undefined;
   ulProspectorId?: number;
   ulPortfolio?: boolean;
   privateFeatureToggles: FeatureToggle[];
   documents?: RelatedDocument[];
   user?: User;
   accessToken?: string;
}

// These languages are hardcoded and remain the same over all channels as these are based on ul Prospector data
export const availableLanguageCodes = ['en', 'de', 'es', 'fr', 'ja', 'pt', 'ru', 'zh'];

export const QuickTdsLink: FC<Props> = ({
   isPublicTdsDownloadFeatureEnabled,
   isPublicShowTdsUomToggleEnabled,
   user,
   accessToken,
   ulPortfolio,
   documentAnchor,
   productId,
   uomCollection,
   productName,
   actions,
   locale,
   ulProspectorId,
   privateFeatureToggles,
   documents
}) => {
   const translate = useTranslations();
   const language = getLanguageCode(locale);
   const { downloadPublicTdsDocument, downloadDocument, getAllOrderedProducts } = actions;
   const [documentLanguage, setDocumentLanguage] = useState<DropdownOption>({ id: 'en', value: 'English' });
   const [documentUom, setDocumentUom] = useState<DropdownOption>(() => {
      const uomOption = uomCollection?.find((uom) => uom.default);
      return uomOption
         ? { ...uomOption, value: translate(uomOption.value) }
         : {
              id: 'metric',
              value: translate('unit-of-measure.metric')
           };
   });
   const { dispatch } = useDialogContext();
   const { phone } = useBreakpoint();

   const isAuthenticated = !!user;

   const hasShowTdsUomPermission = isFeatureEnabled(privateFeatureToggles, 'product-documents-tds-uom');
   const hasSharePermission = isFeatureEnabled(privateFeatureToggles, 'share-page');

   const isSalesRep = useMemo(() => !!user?.groups?.includes(UserGroups.ravagoSalesRepresentative), [user?.groups]);

   const availableUomOptions: NormalizedUnitOfMeasure[] = useMemo(
      () =>
         uomCollection?.sort(firstBy((l) => l, { ignoreCase: true }))?.map((uom) => ({
            ...uom,
            value: translate(uom.value) as any
         })) ?? [],
      [uomCollection, locale]
   );

   const availableLanguagesOptions: DropdownOption[] = useMemo(
      () =>
         availableLanguageCodes
            .map(
               (key): DropdownOption => ({
                  id: key,
                  value: getLanguageNameFromLanguageCode(key, locale)
               })
            )
            .sort(firstBy((l) => l, { ignoreCase: true })),
      [locale]
   );

   const setDownloadDialogVisibility = (openOrClose: 'open' | 'close') => {
      dispatch?.({
         type: `${openOrClose}_dialog`,
         value: { dialogType: 'document-is-downloading', title: translate('seller-widget.tds') }
      });
   };

   const trackQuickTdsDownload = (authaccessToken: string, document: { id: string; title: string }) => {
      Promise.all([getAllOrderedProducts(authaccessToken), QuotationUtils.getQuotations()]).then(([products, quotations]) => {
         const productIds = products.content.filter((prod) => !!prod.baseProductId).map((product) => product.baseProductId!.toString());
         if (quotations) {
            productIds.push(
               ...quotations.filter((quotation) => !!quotation.product.baseProductId).map((quotation) => quotation.product.baseProductId!.toString())
            );
         }

         const trackingInfo = {
            product: {
               id: productId,
               name: productName
            },
            document,
            correlationId: CorrelationService.getCorrelationId('dpcid')
         };
         if (productIds.includes(productId ?? '')) {
            TrackingUtils.track('tds_download_previously_purchased', trackingInfo);
         } else {
            TrackingUtils.track('tds_download_new_lead', trackingInfo);
         }
      });
   };

   const setCorrectTdsDocuments = (relatedDocuments?: RelatedDocument[]) => {
      if (!relatedDocuments || relatedDocuments.length === 0) return [];
      const tdsDocumentsCurrentLanguage = relatedDocuments.filter((tdsDocument) => tdsDocument.language.toLowerCase() === language);
      const tdsDocumentsEnglish = relatedDocuments.filter((tdsDocument) => tdsDocument.language.toLowerCase() === 'en');

      if (tdsDocumentsCurrentLanguage.length > 0) {
         return tdsDocumentsCurrentLanguage;
      }
      if (tdsDocumentsEnglish.length > 0) {
         return tdsDocumentsEnglish;
      }
      return [];
   };

   const tdsDocuments = useMemo(() => setCorrectTdsDocuments(documents), [documents]);
   const handleLanguageChange = (lan: DropdownOption | undefined) => {
      if (lan) setDocumentLanguage(lan);
   };

   const handleUOMChange = (uom: DropdownOption | undefined) => {
      if (uom) setDocumentUom(uom);
   };

   const downloadTdsFromUl = async () => {
      if (!ulProspectorId) return;

      setDownloadDialogVisibility('open');
      try {
         const blob = await downloadPublicTdsDocument(
            documentLanguage.id,
            ulProspectorId.toString(),
            CorrelationService.generateCorrelationId('dpcid'),
            documentUom.id as 'imperial' | 'metric' | undefined
         );
         if (blob.length) {
            const fileName = DocumentNameUtils.determineGenericTDSFileName(documentLanguage.id, productName);
            saveAs(blob, fileName);
         }
      } catch {
         notification(translate('shipment.documents.download-error'));
      } finally {
         if (isAuthenticated && accessToken) {
            trackQuickTdsDownload(accessToken, { id: ulProspectorId.toString(), title: 'TDS' });
         }
         setDownloadDialogVisibility('close');
      }
   };

   const downloadTdsFromDms = async () => {
      if (!tdsDocuments?.length || !accessToken) {
         return;
      }
      const { id: tdsDocumentId, description: tdsDocumentDescription } = tdsDocuments[0];

      setDownloadDialogVisibility('open');
      try {
         const blob = await downloadDocument(
            `products/${productId}/documents/${tdsDocumentId}`,
            accessToken,
            CorrelationService.generateCorrelationId('dpcid')
         );
         if (blob?.length) {
            saveAs(blob, tdsDocumentDescription);
         }
      } catch {
         notification(translate('shipment.documents.download-error'));
      } finally {
         trackQuickTdsDownload(accessToken, { id: tdsDocumentId, title: tdsDocumentDescription });
         setDownloadDialogVisibility('close');
      }
   };

   const scrollToDocuments = () => {
      if (!documentAnchor) return;
      document.getElementById(documentAnchor)?.scrollIntoView({ behavior: 'smooth' });
   };

   const dmsQuickLink = () =>
      tdsDocuments ? (
         <>
            {tdsDocuments.length === 1 && isAuthenticated && (
               <Box>
                  { }
                  <Link onClick={() => downloadTdsFromDms()} variant="body">
                     {translate('products-technical-data-sheet')}
                     <Icon icon="download" type="default" size="body" color="primary" />
                  </Link>
               </Box>
            )}
            {tdsDocuments.length !== 1 && !(isSalesRep && ulProspectorId) && (
               <Box direction="column" items="end" gap="none">
                  {tdsDocuments.length > 1 && (
                     <Typography color="weak" variant="body">
                        {translate('product.documents.dms-multiple-docs')}
                     </Typography>
                  )}
                  {tdsDocuments.length === 0 && (
                     <Typography color="weak" variant="body">
                        {translate('product.documents.technical-data-sheet-not-available')}
                     </Typography>
                  )}
                  {documentAnchor && documentAnchor !== '' && (
                      
                     <Link color="primary" onClick={() => scrollToDocuments()} variant="body">
                        {translate('product.go-to-all-documents')}
                        <Icon icon="long-arrow-down" type="default" size="body" color="primary" />
                     </Link>
                  )}
               </Box>
            )}
         </>
      ) : (
         <Spinner />
      );

   if (!isAuthenticated && !isPublicTdsDownloadFeatureEnabled) {
      return null;
   }

   return (
      <Box items={phone ? 'start' : 'end'} direction="column" gap="xs">
         {!!ulPortfolio && !!ulProspectorId ? (
            <Box gap="sm" items="center" width="100%">
               <Dropdown
                  value={documentLanguage}
                  emptyOption={false}
                  label={translate('products-technical-data-sheet')}
                  onChange={(lan) => handleLanguageChange(lan)}
                  id={`${productId}-language`}
                  options={availableLanguagesOptions}
               />
               {hasShowTdsUomPermission ||
                  (!isAuthenticated && isPublicShowTdsUomToggleEnabled && (
                     <Dropdown
                        value={documentUom}
                        emptyOption={false}
                        id={`${productId}-uom`}
                        options={availableUomOptions}
                        label={translate('product.documents.uom')}
                        onChange={(uom) => handleUOMChange(uom)}
                     />
                  ))}
               <Button variant="secondary" onClick={() => downloadTdsFromUl()} disabled={!tdsDocuments}>
                  <Icon icon="download" />
               </Button>
               {hasSharePermission && isAuthenticated && (
                  <ShareAnchor
                     squareButton
                     backdrop
                     variant="primary"
                     pageName={translate('seller-widget.tds')}
                     documentInfo={{
                        detailPageName: productName,
                        name: translate('seller-widget.tds'),
                        id: ulProspectorId.toString()
                     }}
                  />
               )}
            </Box>
         ) : (
            dmsQuickLink()
         )}
         {isSalesRep && !!ulProspectorId && (
            <Box gap="sm" items="center">
               <Icon color="primary" icon="external-link" />
               <Typography component="p" variant="body">
                  <Link color="primary" href={`https://materials.ulprospector.com/en/document?e=${ulProspectorId}`} target="_blank">
                     {translate('product.ul-prospector-link')}
                  </Link>
               </Typography>
            </Box>
         )}
      </Box>
   );
};
